/**
 * LA11 — LA-2/4/5/13/19: metadata and consent over LA10's existing seams.
 * No model catalog, token field or download transport lives here.
 * LA15 binds the service's authorized device report to the selected Local Node.
 * F1/F2/F3: dispatch, health and per-model confirmation call the same
 * server routes; the server revalidates every fact before any dispatch.
 */
import { ApiClientError, requestJson, responseArray, responseRecord } from './http';

export { ApiClientError as LocalAiApiError };

export type LocalAiState = 'one_click' | 'confirm_required' | 'browse_only' | 'disabled';
export type LocalAiConnector = 'resolve' | 'ollama' | 'docker' | 'comfyui';

export interface LocalAiLicence {
  id: string | null;
  url: string | null;
  version_or_date: string | null;
  tier: string | null;
  summary: string | null;
}

export interface LocalAiAcceptance {
  licence_url: string;
  licence_version: string;
  accepted_at: number;
  digest: string;
}

export interface LocalAiFrictionProof {
  model_id: string;
  notice_digest: string;
  phrase_digest: string;
  confirmed_at: number;
}

export interface LocalAiItem {
  kind: 'connector' | 'capability' | 'model';
  item_id: string;
  state: LocalAiState;
  reason_code: string;
  reason_text: string;
  connector: LocalAiConnector | null;
  capabilities: string[];
  included_in_products: string[];
  browse_url: string | null;
  installed: boolean;
  usable: boolean;
  tested: boolean;
  licence: LocalAiLicence | null;
  file_ids: string[] | null;
  size_bytes: number | null;
  store_id: string | null;
  token_present: boolean;
  gated: boolean | null;
  token_required: boolean | null;
  mature: boolean | null;
  adult_attested: boolean;
  mature_attestation_ref: string | null;
  acceptance_required: boolean;
  licence_acceptance: LocalAiAcceptance | null;
  friction_required: boolean;
  friction_verified: boolean;
  download_ready: boolean;
  notice: string | null;
  confirmation_phrase: string | null;
  friction_proof: LocalAiFrictionProof | null;
}

export interface LocalAiCatalog {
  context_kind: 'adult' | 'child' | null;
  items: LocalAiItem[];
}

export interface LocalAiAcceptanceInput {
  model_id: string;
  file_ids: string[];
  licence_url: string;
  licence_version: string;
}

export type LocalAiFrictionInput =
  | { model_id: string; action: 'show_notice' }
  | { model_id: string; action: 'check_licence'; checked: boolean }
  | { model_id: string; action: 'confirm_phrase'; phrase: string };

export interface LocalAiFrictionState {
  model_id: string;
  stage: 'notice_shown' | 'licence_checked' | 'confirmed';
  notice: string;
  confirmation_phrase: string;
  proof: LocalAiFrictionProof | null;
}

/** LA-19: unchanged LA04 wire form; a plan is not signed approval or dispatch. */
export interface LocalAiDownloadRequest {
  request_id: string;
  model_id: string;
  file_ids: string[];
  store_id: string;
  licence_acceptance: LocalAiAcceptance;
  token_mode: 'none' | 'device_hf_token';
  friction?: LocalAiFrictionProof;
  mature_attestation_ref?: string;
}

/** LA-1/5/15: LA04 device progress, supplied by the existing dispatch owner. */
export interface LocalAiDownloadProgress {
  request_id: string;
  bytes_done: number;
  state: 'queued' | 'downloading' | 'verifying' | 'placed' | 'failed' | 'refused';
  reason_code: string | null;
}

const BASE = '/v1/local-ai';
const CONNECTORS = ['resolve', 'ollama', 'docker', 'comfyui'] as const;

function malformed(): never {
  throw new ApiClientError('malformed', 200, 'malformed_response', 'Local AI details could not be read.');
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function requiredText(value: unknown): string {
  return text(value) ?? malformed();
}

function strings(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((item) => typeof item === 'string' && item.trim()) ? [...value] : null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function fact(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** LA-6: exact public host; refuse credentials, alternate ports and lookalikes. */
export function localAiBrowseUrl(value: string | null): string | null {
  const safe = localAiLicenceUrl(value);
  return safe && new URL(safe).hostname === 'huggingface.co' ? safe : null;
}

export function localAiLicenceUrl(value: string | null): string | null {
  if (!value || /[\s\\\u0000-\u001f\u007f]/.test(value)) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && parsed.hostname && !parsed.username && !parsed.password && !parsed.port
      ? value : null;
  } catch {
    return null;
  }
}

function acceptance(value: unknown): LocalAiAcceptance | null {
  if (value == null) return null;
  const row = responseRecord(value);
  const url = localAiLicenceUrl(text(row.licence_url));
  const version = text(row.licence_version);
  const at = count(row.accepted_at);
  if (!url || !version || at === null || typeof row.digest !== 'string' || !/^[a-f0-9]{64}$/.test(row.digest)) malformed();
  return { licence_url: url, licence_version: version, accepted_at: at, digest: row.digest };
}

function proof(value: unknown): LocalAiFrictionProof | null {
  if (value == null) return null;
  const row = responseRecord(value);
  const at = count(row.confirmed_at);
  if (at === null || typeof row.notice_digest !== 'string' || !/^[a-f0-9]{64}$/.test(row.notice_digest)
      || typeof row.phrase_digest !== 'string' || !/^[a-f0-9]{64}$/.test(row.phrase_digest)) malformed();
  return { model_id: requiredText(row.model_id), notice_digest: row.notice_digest,
    phrase_digest: row.phrase_digest, confirmed_at: at };
}

function item(value: unknown): LocalAiItem {
  const row = responseRecord(value);
  if (row.kind !== 'connector' && row.kind !== 'capability' && row.kind !== 'model') malformed();
  if (row.state !== 'one_click' && row.state !== 'confirm_required' && row.state !== 'browse_only' && row.state !== 'disabled') malformed();
  const licence = row.licence == null ? null : responseRecord(row.licence);
  return {
    kind: row.kind, item_id: requiredText(row.item_id), state: row.state,
    reason_code: requiredText(row.reason_code), reason_text: requiredText(row.reason_text),
    connector: CONNECTORS.find((name) => name === row.connector) ?? null,
    capabilities: strings(row.capabilities) ?? [], included_in_products: strings(row.included_in_products) ?? [],
    browse_url: localAiBrowseUrl(text(row.browse_url)), installed: row.installed === true, usable: row.usable === true,
    tested: row.tested === true,
    licence: licence ? { id: text(licence.id), url: localAiLicenceUrl(text(licence.url)),
      version_or_date: text(licence.version_or_date), tier: text(licence.tier), summary: text(licence.summary) } : null,
    file_ids: strings(row.file_ids), size_bytes: count(row.size_bytes), store_id: text(row.store_id),
    token_present: row.token_present === true, gated: fact(row.gated), token_required: fact(row.token_required),
    mature: fact(row.mature), adult_attested: row.adult_attested === true,
    mature_attestation_ref: text(row.mature_attestation_ref), acceptance_required: row.acceptance_required !== false,
    licence_acceptance: acceptance(row.licence_acceptance), friction_required: row.friction_required === true,
    friction_verified: row.friction_verified === true, download_ready: row.download_ready === true,
    notice: text(row.notice), confirmation_phrase: text(row.confirmation_phrase), friction_proof: proof(row.friction_proof),
  };
}

/** F2: the selected device is a selector only; the server re-authorizes it. */
function deviceQuery(deviceId?: string | null): string {
  return deviceId ? '?device_id=' + encodeURIComponent(requiredText(deviceId)) : '';
}

export async function fetchLocalAiCatalog(deviceId?: string | null, storeId?: string | null): Promise<LocalAiCatalog> {
  const query = deviceQuery(deviceId);
  const store = storeId ? `${query ? '&' : '?'}store_id=${encodeURIComponent(requiredText(storeId))}` : '';
  const payload = responseRecord(await requestJson(`${BASE}/catalog${query}${store}`, { method: 'GET' }));
  return { context_kind: payload.context_kind === 'adult' || payload.context_kind === 'child' ? payload.context_kind : null,
    items: responseArray(payload, 'items').map(item) };
}

export async function recordLocalAiAcceptance(input: LocalAiAcceptanceInput): Promise<LocalAiAcceptance> {
  // LA-4/5: construct closed bodies, including when an untyped caller supplies extras.
  const files = strings(input.file_ids) ?? malformed();
  const url = localAiLicenceUrl(text(input.licence_url)) ?? malformed();
  const payload = await requestJson(`${BASE}/acceptance`, { method: 'POST', body: JSON.stringify({
    model_id: requiredText(input.model_id), file_ids: files, licence_url: url,
    licence_version: requiredText(input.licence_version),
  }) });
  const result = acceptance(responseRecord(payload).licence_acceptance) ?? malformed();
  if (result.licence_url !== url || result.licence_version !== input.licence_version) malformed();
  return result;
}

export async function recordLocalAiFriction(input: LocalAiFrictionInput): Promise<LocalAiFrictionState> {
  if (!['show_notice', 'check_licence', 'confirm_phrase'].includes(input.action)
      || (input.action === 'check_licence' && typeof input.checked !== 'boolean')
      || (input.action === 'confirm_phrase' && typeof input.phrase !== 'string')) malformed();
  const body = { model_id: requiredText(input.model_id), action: input.action,
    ...(input.action === 'check_licence' ? { checked: input.checked } : {}),
    ...(input.action === 'confirm_phrase' ? { phrase: input.phrase } : {}) };
  const row = responseRecord(await requestJson(`${BASE}/friction-proof`, { method: 'POST', body: JSON.stringify(body) }));
  if (row.stage !== 'notice_shown' && row.stage !== 'licence_checked' && row.stage !== 'confirmed') malformed();
  const result: LocalAiFrictionState = { model_id: requiredText(row.model_id), stage: row.stage, notice: requiredText(row.notice),
    confirmation_phrase: requiredText(row.confirmation_phrase), proof: proof(row.proof) };
  if (result.model_id !== input.model_id || (result.stage === 'confirmed' && result.proof?.model_id !== input.model_id)) malformed();
  return result;
}

function downloadBody(input: LocalAiDownloadRequest) {
  // LA-5/19: token_mode is the closed enum, never a token value. No device path or URL.
  if (input.token_mode !== 'none' && input.token_mode !== 'device_hf_token') malformed();
  const consent = acceptance(input.licence_acceptance) ?? malformed();
  const friction = proof(input.friction);
  const body = {
    request_id: requiredText(input.request_id), model_id: requiredText(input.model_id),
    file_ids: strings(input.file_ids) ?? malformed(), store_id: requiredText(input.store_id),
    licence_acceptance: { licence_url: consent.licence_url, licence_version: consent.licence_version,
      accepted_at: consent.accepted_at, digest: consent.digest }, token_mode: input.token_mode,
    ...(friction ? { friction: { model_id: friction.model_id, notice_digest: friction.notice_digest,
      phrase_digest: friction.phrase_digest, confirmed_at: friction.confirmed_at } } : {}),
    ...(input.mature_attestation_ref ? { mature_attestation_ref: requiredText(input.mature_attestation_ref) } : {}),
  };
  return { body, consent, friction };
}

export async function planLocalAiDownload(input: LocalAiDownloadRequest, deviceId?: string | null): Promise<LocalAiDownloadRequest> {
  const { body, consent, friction } = downloadBody(input);
  const row = responseRecord(await requestJson(`${BASE}/download-plan${deviceQuery(deviceId)}`, { method: 'POST', body: JSON.stringify(body) }));
  const files = strings(row.file_ids);
  if (!files || (row.token_mode !== 'none' && row.token_mode !== 'device_hf_token')) malformed();
  const result: LocalAiDownloadRequest = {
    request_id: requiredText(row.request_id), model_id: requiredText(row.model_id), file_ids: files,
    store_id: requiredText(row.store_id), licence_acceptance: acceptance(row.licence_acceptance) ?? malformed(),
    token_mode: row.token_mode,
  };
  const frictionProof = proof(row.friction);
  if (frictionProof) result.friction = frictionProof;
  const matureRef = text(row.mature_attestation_ref);
  if (matureRef) result.mature_attestation_ref = matureRef;
  if (result.request_id !== input.request_id || result.model_id !== input.model_id || result.store_id !== input.store_id
      || JSON.stringify([...result.file_ids].sort()) !== JSON.stringify([...input.file_ids].sort())
      || result.token_mode !== input.token_mode || JSON.stringify(result.licence_acceptance) !== JSON.stringify(consent)
      || JSON.stringify(result.friction) !== JSON.stringify(friction ?? undefined)
      || result.mature_attestation_ref !== body.mature_attestation_ref) malformed();
  return result;
}

export interface LocalAiDispatchResult {
  job_id: string;
  state: string;
}

/** V88-R1: the caller's own session download/health job, read through the
 * existing bridge work store. `progress` carries the LA04 device progress
 * for download jobs; it is null for health jobs and before any report. */
export interface LocalAiJobStatus {
  job_id: string;
  status: string;
  outcome: string | null;
  reason_code: string | null;
  request_id: string | null;
  progress: LocalAiDownloadProgress | null;
}

function jobProgress(value: unknown): LocalAiDownloadProgress | null {
  if (value == null) return null;
  const row = responseRecord(value);
  const bytes = count(row.bytes_done);
  if (bytes === null) malformed();
  if (row.state !== 'queued' && row.state !== 'downloading' && row.state !== 'verifying'
      && row.state !== 'placed' && row.state !== 'failed' && row.state !== 'refused') malformed();
  const reason = text(row.reason_code);
  if ((row.state === 'failed' || row.state === 'refused') && !reason) malformed();
  return { request_id: requiredText(row.request_id), bytes_done: bytes, state: row.state, reason_code: reason };
}

export async function fetchLocalAiJobStatus(jobId: string): Promise<LocalAiJobStatus> {
  const id = requiredText(jobId);
  const row = responseRecord(await requestJson(`${BASE}/jobs/${encodeURIComponent(id)}`, { method: 'GET' }));
  return {
    job_id: requiredText(row.job_id), status: requiredText(row.status),
    outcome: text(row.outcome), reason_code: text(row.reason_code),
    request_id: text(row.request_id), progress: jobProgress(row.progress),
  };
}

function dispatchResult(payload: unknown): LocalAiDispatchResult {
  const row = responseRecord(payload);
  return { job_id: requiredText(row.job_id), state: requiredText(row.state) };
}

/** F1 (LA-15/19): hand one user-confirmed, server-planned request to the
 * existing Bridge dispatch for the device the user selected. The server
 * revalidates the whole plan before anything is queued. */
export async function dispatchLocalAiDownload(deviceId: string, input: LocalAiDownloadRequest): Promise<LocalAiDispatchResult> {
  const { body } = downloadBody(input);
  const payload = await requestJson(`${BASE}/download-dispatch`, { method: 'POST', body: JSON.stringify({
    device_id: requiredText(deviceId), ...body,
  }) });
  return dispatchResult(payload);
}

/** F2 (LA-7/19): ask the selected device for fresh connector health through
 * the existing local.ai.health operation; the catalog reads its receipt. */
export async function requestLocalAiHealth(deviceId: string): Promise<LocalAiDispatchResult> {
  const payload = await requestJson(`${BASE}/health`, { method: 'POST', body: JSON.stringify({
    device_id: requiredText(deviceId), idempotency_key: globalThis.crypto.randomUUID(),
  }) });
  return dispatchResult(payload);
}

/** F3 (LA-11): record the caller's own per-model confirmation. This only
 * supplies the existing consent; it never attests or changes context. */
export async function recordLocalAiMatureConfirmation(modelId: string): Promise<void> {
  const row = responseRecord(await requestJson(`${BASE}/mature-confirmation`, { method: 'POST', body: JSON.stringify({
    model_id: requiredText(modelId),
  }) }));
  if (row.model_id !== modelId || row.mature_confirmed !== true) malformed();
}

export interface LocalAiAdultWorkspace {
  enabled: boolean;
  can_manage: boolean;
  can_enable?: boolean;
  updated_at: number | null;
}

function adultWorkspace(payload: unknown): LocalAiAdultWorkspace {
  const row = responseRecord(payload);
  if (typeof row.enabled !== 'boolean' || typeof row.can_manage !== 'boolean') malformed();
  return {
    enabled: row.enabled, can_manage: row.can_manage,
    ...(typeof row.can_enable === 'boolean' ? { can_enable: row.can_enable } : {}),
    updated_at: typeof row.updated_at === 'number' ? row.updated_at : null,
  };
}

/** Hosted adult-workspace setting for the caller's own workspace. The server
 * decides `can_manage` (owner/admin only); the browser never supplies a tenant. */
export async function fetchLocalAiAdultWorkspace(): Promise<LocalAiAdultWorkspace> {
  return adultWorkspace(await requestJson(`${BASE}/adult-workspace`, { method: 'GET' }));
}

/** Owner/admin only. Enabling needs the existing 18+ attestation; turning it
 * off is always allowed. Per-model confirmation is still required afterwards. */
export async function setLocalAiAdultWorkspace(enabled: boolean): Promise<LocalAiAdultWorkspace> {
  const result = adultWorkspace(await requestJson(`${BASE}/adult-workspace`, {
    method: 'POST', body: JSON.stringify({ enabled }),
  }));
  if (result.enabled !== enabled) malformed();
  return result;
}
